/**
 * Financial approval of service requests — one queue, two desks.
 *
 * Contracts (SERVICE_REQUEST_APPROVE) decides the money behind every request.
 * Patient reception (DIAGNOSTICS_APPROVE) decides labs and scans, so a patient
 * sent for a blood test or an X-ray is cleared at the counter instead of
 * waiting on Contracts. The server scopes the queue to what each desk may
 * decide (routes/contracts.js approvalScope) and refuses anything outside it,
 * so the same screen serves both.
 *
 * Approving freezes a quote onto the request: what the cover pays and what the
 * patient owes, computed by the server from the patient's boxes
 * (lib/coverage.js). The dialog shows that quote before the button is pressed.
 */

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, EmptyState, ErrorState, Field, Icon, Input, Modal, Spinner, Textarea } from '../../components/common';
import { contractsApi, patientsApi, requestsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePoll } from '../../hooks/usePolling';
import { RequestCard } from './RequestCard';
import { NoteDialog } from './NoteDialog';
import { formatMoney } from './labels';

/** Approve with prices confirmed and the patient's share previewed. */
export function ApproveDialog({ request, onClose, onDone }) {
  const { success, error: toastError } = useNotifications();
  const [prices, setPrices] = useState(() =>
    (request.items || []).map((i) => (i.unitPrice === null || i.unitPrice === undefined ? '' : String(i.unitPrice))),
  );
  const [note, setNote] = useState('');
  const [quote, setQuote] = useState(request.quote || null);
  const [busy, setBusy] = useState(false);

  const priced = prices.length > 0 && prices.every((p) => p !== '' && Number(p) >= 0);

  useEffect(() => {
    if (!priced) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      patientsApi
        .quote(request.hospitalPatientId, {
          department: request.department,
          priority: request.priority,
          items: (request.items || []).map((item, i) => ({ ...item, unitPrice: Number(prices[i]) })),
        })
        .then((q) => !cancelled && setQuote(q))
        .catch(() => !cancelled && setQuote(null));
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [prices, priced, request]);

  const approve = async () => {
    setBusy(true);
    try {
      await requestsApi.finance(request.id, {
        decision: 'approved',
        note: note.trim() || null,
        prices: prices.map((p, index) => (p === '' ? null : { index, unitPrice: Number(p) })).filter(Boolean),
      });
      success('تمت الموافقة المالية — وصل الطلب للقسم');
      onDone();
      onClose();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`موافقة مالية — ${request.title}`}
      icon="shield-check"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            رجوع
          </Button>
          <Button variant="success" icon="check-circle-2" loading={busy} onClick={approve}>
            موافقة
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <RequestCard request={request} showMoney />
        <div>
          <p className="mb-2 text-xs font-bold text-slate-400">أسعار البنود (من نموذج التأمين أو قائمة الأسعار)</p>
          <div className="space-y-2">
            {(request.items || []).map((item, i) => (
              <div key={i} className="grid grid-cols-[1fr_9rem] items-center gap-2">
                <span className="text-sm text-slate-200">
                  {item.name}
                  {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                </span>
                <Input
                  type="number"
                  min="0"
                  value={prices[i]}
                  onChange={(e) => setPrices((list) => list.map((p, j) => (j === i ? e.target.value : p)))}
                  placeholder="سعر الوحدة"
                  aria-label={`سعر ${item.name}`}
                />
              </div>
            ))}
          </div>
          {!priced && (
            <p className="mt-2 text-xs text-slate-500">يمكن الموافقة دون أسعار — تُسعّر الحسابات الطلب لاحقاً.</p>
          )}
        </div>

        {quote && (
          <div className="rounded-xl border border-purple-500/25 bg-purple-500/[0.07] p-3" aria-live="polite">
            <div className="flex flex-wrap gap-2">
              <Badge className="border-white/10 bg-white/5 text-slate-200">الإجمالي {formatMoney(quote.total)}</Badge>
              <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200">يغطيه التأمين {formatMoney(quote.covered)}</Badge>
              <Badge className="border-amber-500/40 bg-amber-500/15 text-amber-100">على المريض {formatMoney(quote.patientShare)}</Badge>
            </div>
            <ul className="mt-2 space-y-0.5 text-xs text-slate-300">
              {quote.lines.map((l, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span>{l.label}</span>
                  <span className="text-slate-400">{l.note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Field label="ملاحظة (اختياري)">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

export function FinanceQueue({ onChanged = () => {}, onOpenPatient = null, emptyHint = null }) {
  const { success, error: toastError } = useNotifications();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async () => {
    const list = await contractsApi.queue(status);
    setRows(list);
    setError(null);
  }, [status]);

  useEffect(() => {
    setRows(null);
    load().catch(setError);
  }, [load]);

  usePoll(load, status === 'pending' ? 20000 : null, { immediate: false });

  const reject = async (note) => {
    try {
      await requestsApi.finance(rejecting.id, { decision: 'rejected', note });
      success('تم الرفض المالي وأُبلغ المرسل بالسبب');
      setRejecting(null);
      await load();
      onChanged();
    } catch (err) {
      toastError(err.message);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          ['pending', 'بانتظار القرار', 'hourglass'],
          ['approved', 'تمت الموافقة', 'check-circle-2'],
          ['rejected', 'مرفوضة', 'x-circle'],
        ].map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            onClick={() => setStatus(id)}
            aria-pressed={status === id}
            className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-4 text-sm font-bold ${
              status === id ? 'border-purple-500/50 bg-purple-500/15 text-purple-100' : 'border-white/10 text-slate-400 hover:text-white'
            }`}
          >
            <Icon name={icon} size={16} /> {label}
          </button>
        ))}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => load().catch(setError)} />
      ) : !rows ? (
        <Spinner label="جارٍ تحميل الطلبات" variant="inline" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="shield-check"
          title={status === 'pending' ? 'لا توجد طلبات بانتظار الموافقة' : 'لا يوجد شيء هنا'}
          message={status === 'pending' ? emptyHint : null}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {rows.map((r) => (
            <RequestCard key={r.id} request={r} showMoney onOpenPatient={onOpenPatient ? () => onOpenPatient(r.hospitalPatientId) : undefined}>
              {status === 'pending' && r.status !== 'cancelled' && (
                <>
                  <Button variant="success" icon="check" onClick={() => setApproving(r)}>
                    موافقة مالية
                  </Button>
                  <Button variant="danger" icon="x-circle" onClick={() => setRejecting(r)}>
                    رفض
                  </Button>
                </>
              )}
              {r.financeByName && status !== 'pending' && (
                <span className="text-xs text-slate-400">بواسطة {r.financeByName}</span>
              )}
            </RequestCard>
          ))}
        </div>
      )}

      {approving && (
        <ApproveDialog
          key={approving.id}
          request={approving}
          onClose={() => setApproving(null)}
          onDone={() => {
            load().catch(setError);
            onChanged();
          }}
        />
      )}
      <NoteDialog
        open={Boolean(rejecting)}
        onClose={() => setRejecting(null)}
        onSubmit={reject}
        title="رفض مالي"
        label="سبب الرفض — يصل للطبيب أو المكتب الذي أرسل الطلب"
        destructive
        confirmLabel="رفض الطلب"
      />
    </div>
  );
}

export default FinanceQueue;
